<template lang="pug">
div(
    :class="{ filled: filled, disabled: disabled }"
    class="tl-input-document-upload"
)
    div(
        v-if="title"
        class="tl-title"
    ) {{ title }}

    div(class="tl-input-wrapper")
        div(class="tl-label")
            div(:class="(documentsError || error) && 'error'") {{ label }}

        div(
            class="documents"
            data-testid="documents"
        )
            div(
                v-for="(document, index) in Object.values(modelState)"
                :key="index"
                class="document"
                :class="{ 'disabled': disabled || document.disabled || document.loading }"
                data-testid="document"
            )
                div(
                    class="document-type"
                )
                    InputDropdown(
                        v-if="!document.verified"
                        :initial-value="documentTypeDropdownItems.find(i => i.value === document.documentType)"
                        :label="t('input-document-upload.select-document-type')"
                        :items="documentTypeDropdownItems"
                        :item-label="(i) => i.label"
                        :item-value="(i) => i.value"
                        :disabled="disabled || document.disabled || document.loading"
                        test-id="document-type-dropdown"
                        @update:model-value="handleDocumentTypeChange(document, $event)"
                        @clear="handleDocumentTypeChange(document)"
                    )
                    span(
                        v-else
                        class="document-type-value"
                        data-testid="document-type"
                    )
                        | {{ t('input-document-upload.document-type.' + document?.documentType?.toLowerCase().replace(/_/g, '-')) }}
                ComplyCubeFileUpload(
                    v-model="document.files"
                    :sides="document.sides"
                    :upload-limit="document.sides"
                    :extensions="extensions"
                    :loading="document.loading"
                    :progress="document.progress"
                    :disabled="!document.documentType || disabled || document.disabled"
                    :min-file-size="minFileSize"
                    :max-file-size="maxFileSize"
                    :label="t('input-document-upload.document.title', {id: index + 1 })"
                    :error="document.error"
                    data-testid="file-upload"
                    @error="handleDocumentError(document, $event)"
                )
                i(
                    class="remove-document mi-close"
                    data-testid="remove-document-trigger"
                    @click.stop="removeDocument(document)"
                )

            ActionButton(
                :label="`+ ${ documentsLength > 0 ? t('input-document-upload.document.add-another') : t('input-document-upload.document.add') }`"
                secondary
                :disabled="disabled"
                test-id="add-document-trigger"
                @click="addDocument"
            )

        ErrorText(
            v-if="error?.length"
            class="general-error"
            data-testid="documents-error"
            :error="error"
        )
</template>

<script setup>
import {
    ref, watch, computed, onMounted,
} from 'vue'
import { v4 as uuid } from 'uuid'
import { useI18n } from 'widget-components/utilities/i18n'
import InputDropdown from 'widget-components/components/InputDropdown/InputDropdown.vue'
import ActionButton from 'widget-components/components/ActionButton/ActionButton.vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import ComplyCubeFileUpload from '../ComplyCubeFileUpload/ComplyCubeFileUpload.vue'
import equals from 'fast-deep-equal/es6'

const { t } = useI18n()

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({}),
    },
    documentTypes: {
        type: Array,
    },
    disabled: Boolean,
    minFileSize: {
        type: Number,
        default: 0.034 * 1024 * 1024, // 34KB
    },
    maxFileSize: {
        type: Number,
        default: 4 * 1024 * 1024, // 4MB
    },
    extensions: {
        type: String,
        default: 'jpg,jpeg,png',
    },
    error: {
        type: [String, Boolean],
        default: undefined,
    },
    label: String,
    title: String,
})

const documentsError = computed(
    () => Object
        .values(modelState.value)
        .find(document => document.error?.length)?.error || '',
)

const handleDocumentError = (document, error) => {
    document.error = error
}

const documentsLength = computed(
    () => Object
        .values(modelState.value)
        .length || 0,
)

const documentTypeDropdownItems = computed(() =>
    Object
        .values(props.documentTypes)
        .map(item => ({
            ...item,
            label: t('input-document-upload.document-type.' + item.value?.toLowerCase().replace(/_/g, '-')),
        })),
)


const handleDocumentTypeChange = (doc, selectedDocumentType) => {
    doc.documentType = selectedDocumentType?.value
    doc.sides = selectedDocumentType?.sides || 1
    doc.files = {}
}

onMounted(() => {
    watch(documentsError, () => {
        emit('error', documentsError?.value || '')
    }, { immediate: true })
})

const emit = defineEmits(['update:modelValue', 'error'])

const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)

const filled = computed(() => !!Object.values(modelState.value)?.length)
const label = computed(() => props.label)

const addDocument = () => {
    if (!props.documentTypes || props.documentTypes?.length === 0) {
        return
    }

    const id = uuid()
    const doc = {
        documentType: '',
        id,
        files: {},
    }

    modelState.value = {
        ...modelState.value,
        [id]: doc,
    }
}

const removeDocument = (document) => {
    if (props.disabled || document.disabled || document.loading) {
        return
    }
    const newState = modelState.value
    delete newState[document.id]
    modelState.value = newState
}

onMounted(async () => {
    if (props.defaultValue) {
        modelState.value = props.defaultValue
    }
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
}, { deep: true })

watch(modelValue, (val) => {
    if (!equals(val, modelState.value)) {
        modelState.value = val
    }
}, { deep: true })

</script>

<style scoped lang="sass">
@import 'ComplyCubeDocumentUpload'
</style>
